import axios from 'axios';

export const PULSE_API_BASE = '/pulse/api';
export const PULSE_PUBLIC_API_BASE = '/pulse/api/public';

function unwrap(response) {
  return response && response.data ? response.data : response;
}

export async function listPulses(params = {}, options = {}) {
  const base = options.publicMode ? PULSE_PUBLIC_API_BASE : PULSE_API_BASE;
  const response = await axios.get(`${base}/pulses`, { params });
  return unwrap(response);
}

export async function listRecentPulses() {
  const response = await axios.get(`${PULSE_PUBLIC_API_BASE}/recent`);
  return unwrap(response);
}

export async function previewPulseLink(url) {
  const response = await axios.post(`${PULSE_API_BASE}/preview`, { url });
  return unwrap(response);
}

export const PULSE_MEDIA_ACCEPT = '.mp4,video/mp4,.png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif,.pdf,application/pdf';
export const PULSE_IMAGE_ACCEPT = '.png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif';

const PULSE_VIDEO_EXTS = ['mp4'];
const PULSE_IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif'];
const PULSE_DOCUMENT_EXTS = ['pdf'];
const PULSE_VIDEO_MIMES = ['video/mp4'];
const PULSE_IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/gif'];
const PULSE_DOCUMENT_MIMES = ['application/pdf'];

function fileExtension(name) {
  const parts = String(name || '').split('.');
  if (parts.length < 2) {
    return '';
  }
  return parts.pop().toLowerCase();
}

export function pulseFileKind(file) {
  if (!file) {
    return null;
  }
  const ext = fileExtension(file.name);
  const mime = String(file.type || '').toLowerCase();
  if (PULSE_VIDEO_EXTS.indexOf(ext) >= 0 || PULSE_VIDEO_MIMES.indexOf(mime) >= 0) {
    return 'video';
  }
  if (PULSE_IMAGE_EXTS.indexOf(ext) >= 0 || PULSE_IMAGE_MIMES.indexOf(mime) >= 0) {
    return 'image';
  }
  if (PULSE_DOCUMENT_EXTS.indexOf(ext) >= 0 || PULSE_DOCUMENT_MIMES.indexOf(mime) >= 0) {
    return 'document';
  }
  return null;
}

export async function uploadPulseFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${PULSE_API_BASE}/upload`, formData);
  return unwrap(response);
}

export async function createPulse(payload) {
  const response = await axios.post(`${PULSE_API_BASE}/pulses`, payload);
  return unwrap(response);
}

export const PULSE_BULK_URL_LIMIT = 50;

export function parsePulseBulkUrls(text) {
  const seen = {};
  const urls = [];
  const lines = String(text || '').split(/\r?\n/);

  for (let i = 0; i < lines.length; i += 1) {
    let line = String(lines[i] || '').trim();
    if (!line) {
      continue;
    }
    if (!/^https?:\/\//i.test(line)) {
      if (/^\/\//.test(line)) {
        line = `https:${line}`;
      } else if (/^(www\.)?[\w.-]+\.[a-z]{2,}([/:?#].*)?$/i.test(line)) {
        line = `https://${line}`;
      } else {
        continue;
      }
    }
    try {
      const parsed = new URL(line);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        continue;
      }
      if (seen[parsed.href]) {
        continue;
      }
      seen[parsed.href] = true;
      urls.push(parsed.href);
      if (urls.length >= PULSE_BULK_URL_LIMIT) {
        break;
      }
    } catch (e) {
      // Skip lines that are not valid URLs.
    }
  }

  return urls;
}

export async function createPulsesBulk(payload) {
  const response = await axios.post(`${PULSE_API_BASE}/pulses/bulk`, payload);
  return unwrap(response);
}

export async function updatePulse(id, payload) {
  const response = await axios.patch(
    `${PULSE_API_BASE}/pulses/${encodeURIComponent(id)}`,
    payload,
  );
  return unwrap(response);
}

export async function deletePulse(id) {
  const response = await axios.delete(
    `${PULSE_API_BASE}/pulses/${encodeURIComponent(id)}`,
  );
  return unwrap(response);
}

export function youtubeIdFromUrl(url) {
  if (!url) {
    return null;
  }
  const raw = String(url).trim();
  const match = raw.match(
    /(?:youtube(?:-nocookie)?\.com\/(?:.*[?&]v=|(?:embed|shorts|live|v|e)\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/,
  );
  if (match) {
    return match[1];
  }
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    const isYoutube = host === 'youtu.be'
      || host === 'youtube.com'
      || host.endsWith('.youtube.com')
      || host === 'youtube-nocookie.com'
      || host.endsWith('.youtube-nocookie.com');
    if (!isYoutube) {
      return null;
    }
    const fromQuery = parsed.searchParams.get('v');
    if (fromQuery && /^[A-Za-z0-9_-]{11}$/.test(fromQuery)) {
      return fromQuery;
    }
    const pathMatch = parsed.pathname.match(/\/(?:embed|shorts|live|v|e)\/([A-Za-z0-9_-]{11})/);
    if (pathMatch) {
      return pathMatch[1];
    }
    if (host === 'youtu.be') {
      const id = parsed.pathname.replace(/^\//, '').split('/')[0];
      if (/^[A-Za-z0-9_-]{11}$/.test(id)) {
        return id;
      }
    }
  } catch (e) {
    return null;
  }
  return null;
}

export function youtubeThumbnailUrl(id) {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

export function googleDriveIdFromEntry(entry) {
  if (!entry) {
    return null;
  }
  return googleDriveFileIdFromUrl(entry.url)
    || googleDriveFileIdFromUrl(entry.video_url)
    || googleDriveFileIdFromUrl(entry.image_url);
}

export function pulseDirectVideoUrl(entry) {
  if (!entry) {
    return null;
  }
  const url = entry.video_url || entry.url;
  return isDirectVideoUrl(url) ? url : null;
}

export function pulseImageUrl(entry) {
  if (!entry) {
    return null;
  }
  const source = entry.video_url || entry.url || entry.image_url;
  const id = youtubeIdFromUrl(source) || youtubeIdFromUrl(entry.url) || youtubeIdFromUrl(entry.video_url);
  if (id && (!entry.image_url || youtubeIdFromUrl(entry.image_url))) {
    return youtubeThumbnailUrl(id);
  }
  const driveId = googleDriveIdFromEntry(entry);
  if (driveId && (!entry.image_url || isDriveThumbnailUrl(entry.image_url))) {
    return googleDriveThumbnailProxyUrl(driveId);
  }
  if (entry.image_url) {
    return entry.image_url;
  }
  if (driveId) {
    return googleDriveThumbnailProxyUrl(driveId);
  }
  const video = entry.video_url || entry.url;
  if (isDirectVideoUrl(video)) {
    return pulseVideoPosterUrl(video);
  }
  return null;
}

export function pulseVideoPosterUrl(url) {
  const path = mediaPathFromUrl(url).replace(/^\//, '').split('?')[0];
  let relative;
  if (path && /\.(mp4|webm|mov|m4v)$/i.test(path)) {
    relative = `/pulse/api/public/video-poster?file=${encodeURIComponent(path)}`;
  } else {
    relative = `/pulse/api/public/video-poster?url=${encodeURIComponent(url)}`;
  }
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return `${window.location.origin}${relative}`;
  }
  return relative;
}

export function mediaPathFromUrl(url) {
  if (!url) {
    return '';
  }
  try {
    const parsed = new URL(String(url), typeof window !== 'undefined' ? window.location.origin : 'https://example.com');
    return parsed.searchParams.get('file') || parsed.pathname || '';
  } catch (e) {
    return String(url);
  }
}

const UUID_STORED_FILE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\.[a-z0-9]+)?$/i;

export function looksLikeTypedUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return false;
  }
  if (/^(https?:\/\/|\/\/)/i.test(raw)) {
    return true;
  }
  if (/^www\./i.test(raw)) {
    return true;
  }
  if (!/^(?:[\w-]+\.)+[a-z]{2,}([/:?#].*)?$/i.test(raw)) {
    return false;
  }
  if (raw.indexOf('/') === -1 && /\.(mp4|webm|mov|m4v|png|jpe?g|gif|pdf)$/i.test(raw)) {
    return false;
  }
  return true;
}

export function withPulseAssetFilename(url, filename) {
  const raw = String(url || '').trim();
  const name = String(filename || '').trim();
  if (!raw || !name || /[?&]name=/.test(raw)) {
    return raw;
  }
  if (!/\/share(?:\?|$)/.test(raw) || raw.indexOf('file=') === -1) {
    return raw;
  }
  return `${raw}${raw.indexOf('?') >= 0 ? '&' : '?'}name=${encodeURIComponent(name)}`;
}

export function pulseAssetFilename(url) {
  const raw = String(url || '').trim();
  if (!raw) {
    return '';
  }
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://example.com');
    if (/\/pulse\/api\/public\/video-poster\/?$/.test(parsed.pathname)) {
      return 'Video thumbnail';
    }
    const isShare = /\/share\/?$/.test(parsed.pathname);
    const named = String(parsed.searchParams.get('name') || '').trim();
    if (isShare && named) {
      return named;
    }
    const file = String(parsed.searchParams.get('file') || '');
    if (!isShare || file.indexOf('pulse/') !== 0) {
      return '';
    }
    const base = file.split('/').pop() || '';
    if (!base) {
      return '';
    }
    if (UUID_STORED_FILE.test(base)) {
      const ext = fileExtension(base);
      return ext ? `Uploaded file.${ext}` : 'Uploaded file';
    }
    return base;
  } catch (e) {
    return '';
  }
}

export function isDirectVideoUrl(url) {
  if (!url) {
    return false;
  }
  return /\.(mp4|webm|mov|m4v)$/i.test(mediaPathFromUrl(url).split('?')[0]);
}

export function youtubeEmbedUrl(id, { autoplay = false } = {}) {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
  });
  if (autoplay) {
    params.set('autoplay', '1');
    params.set('mute', '1');
  }
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

export function googleDriveFileIdFromUrl(url) {
  if (!url) {
    return null;
  }
  const raw = String(url).trim();
  const fileMatch = raw.match(/\/file\/d\/([A-Za-z0-9_-]+)/);
  if (fileMatch) {
    return fileMatch[1];
  }
  const lh3Match = raw.match(/lh3\.googleusercontent\.com\/d\/([A-Za-z0-9_-]+)/);
  if (lh3Match) {
    return lh3Match[1];
  }
  const thumbMatch = raw.match(/drive\.google\.com\/thumbnail[^'"]*[?&]id=([A-Za-z0-9_-]+)/);
  if (thumbMatch) {
    return thumbMatch[1];
  }
  const proxyMatch = raw.match(/\/pulse\/api\/public\/drive-thumb\/([A-Za-z0-9_-]+)/);
  if (proxyMatch) {
    return proxyMatch[1];
  }
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    const isDrive = host === 'drive.google.com'
      || host === 'docs.google.com'
      || host === 'drive.usercontent.google.com';
    if (!isDrive) {
      return null;
    }
    if (/\/folders\//.test(parsed.pathname)) {
      return null;
    }
    const fromQuery = parsed.searchParams.get('id');
    if (fromQuery && /^[A-Za-z0-9_-]{10,}$/.test(fromQuery)) {
      return fromQuery;
    }
  } catch (e) {
    return null;
  }
  return null;
}

export function isDriveThumbnailUrl(url) {
  if (!url) {
    return false;
  }
  const raw = String(url);
  return /drive\.google\.com\/thumbnail/.test(raw)
    || /lh3\.googleusercontent\.com\/d\//.test(raw);
}

export function googleDriveThumbnailUrl(id) {
  return `https://lh3.googleusercontent.com/d/${id}=w1280`;
}

export function googleDriveThumbnailProxyUrl(id) {
  return `/pulse/api/public/drive-thumb/${encodeURIComponent(id)}`;
}

export function googleDriveEmbedUrl(id, { autoplay = false } = {}) {
  const params = autoplay ? '?autoplay=1' : '';
  return `https://drive.google.com/file/d/${id}/preview${params}`;
}

export function captureVideoPoster(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('missing video'));
      return;
    }
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    let settled = false;
    const finish = (fn, value) => {
      if (settled) {
        return;
      }
      settled = true;
      video.pause();
      video.removeAttribute('src');
      video.load();
      fn(value);
    };
    const timeout = setTimeout(() => finish(reject, new Error('timeout')), 8000);
    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        if (!video.videoWidth) {
          clearTimeout(timeout);
          finish(reject, new Error('no frame'));
          return;
        }
        canvas.getContext('2d').drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
        clearTimeout(timeout);
        finish(resolve, dataUrl);
      } catch (e) {
        clearTimeout(timeout);
        finish(reject, e);
      }
    });
    video.addEventListener('error', () => {
      clearTimeout(timeout);
      finish(reject, new Error('video error'));
    });
    video.addEventListener('loadeddata', () => {
      const mark = Number.isFinite(video.duration) && video.duration > 0
        ? Math.min(0.8, video.duration * 0.1)
        : 0.1;
      try {
        video.currentTime = mark;
      } catch (e) {
        clearTimeout(timeout);
        finish(reject, e);
      }
    });
    video.src = url;
  });
}
